/** @jsxImportSource react */
'use client';

import { motion } from 'motion/react';

interface Outcome {
  index: string;
  stat: string;
  title: string;
  quote: string;
  attribution: string;
  variant: 'mist' | 'ink';
}

const OUTCOMES: Outcome[] = [
  {
    attribution: 'Bobby · MyLife Church',
    index: '01',
    quote: 'Our serve team doubled in a few months.',
    stat: '2×',
    title: 'Your serve team grows.',
    variant: 'mist',
  },
  {
    attribution: 'Johnny · Discover Church',
    index: '02',
    quote: 'More volunteers in months than all of last year.',
    stat: '0',
    title: 'Guests slipping through the cracks.',
    variant: 'ink',
  },
  {
    attribution: 'Johnny · Discover Church',
    index: '03',
    quote: "Once setup was done, it's basically on autopilot.",
    stat: '1 hr',
    title: 'Setup. Then it runs itself.',
    variant: 'mist',
  },
];

export function Outcomes() {
  return (
    <section className="bg-white py-20 sm:py-28 lg:py-[160px]">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-20">
        <motion.div
          className="mb-12 flex max-w-[1280px] items-end justify-between gap-12 sm:mb-16 lg:mb-[72px] lg:gap-20"
          initial={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ margin: '-100px', once: true }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
              <div className="h-px w-8 bg-[color:var(--color-amethyst)]" />
              <span className="text-[13px] font-medium uppercase tracking-[0.12em] text-[color:var(--color-amethyst)]">
                What changes
              </span>
            </div>
            <h2 className="font-bold text-[color:var(--color-ink)] [letter-spacing:-0.032em] [line-height:1.05]">
              <span className="block text-[clamp(40px,5vw,72px)]">
                Three things happen
              </span>
              <span className="block text-[clamp(40px,5vw,72px)] font-light italic [font-family:var(--font-serif)] [letter-spacing:-0.025em]">
                within a few months.
              </span>
            </h2>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {OUTCOMES.map((outcome, i) => (
            <OutcomeCard
              key={outcome.index}
              delay={i * 0.08}
              outcome={outcome}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function OutcomeCard({ delay, outcome }: { outcome: Outcome; delay: number }) {
  const isInk = outcome.variant === 'ink';

  return (
    <motion.div
      className={[
        'flex min-h-[340px] flex-col gap-6 rounded-[20px] p-8 transition-shadow sm:gap-7 sm:p-10 lg:min-h-[380px] lg:p-12',
        isInk
          ? 'bg-[color:var(--color-ink)] text-white'
          : 'bg-[color:var(--color-bg-mist)] text-[color:var(--color-ink)]',
        'hover:shadow-[0_24px_60px_-20px_rgba(15,11,26,0.18)]',
      ].join(' ')}
      initial={{ opacity: 0, y: 24 }}
      transition={{ delay, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      viewport={{ margin: '-50px', once: true }}
      whileHover={{ y: -4 }}
      whileInView={{ opacity: 1, y: 0 }}
    >
      <div className="flex flex-col gap-2">
        <span
          className={[
            'text-[13px] font-medium uppercase tracking-[0.12em]',
            isInk
              ? 'text-[color:var(--color-highlight)]'
              : 'text-[color:var(--color-amethyst)]',
          ].join(' ')}
        >
          {outcome.index}
        </span>
        <span
          className={[
            'font-bold [letter-spacing:-0.045em] [line-height:0.87] text-[clamp(80px,18vw,120px)]',
            isInk
              ? 'text-[color:var(--color-highlight)]'
              : 'text-[color:var(--color-amethyst)]',
          ].join(' ')}
        >
          {outcome.stat}
        </span>
      </div>
      <h3 className="text-[28px] font-bold leading-[1.2] tracking-[-0.018em]">
        {outcome.title}
      </h3>
      <div className="mt-auto border-t border-current pt-6 opacity-100">
        <p
          className={[
            'text-[15px] font-medium italic leading-[1.4]',
            isInk ? 'text-white' : 'text-[color:var(--color-ink)]',
          ].join(' ')}
        >
          &ldquo;{outcome.quote}&rdquo;
        </p>
        <p
          className={[
            'mt-1.5 text-[12px]',
            isInk
              ? 'text-[color:var(--color-gray-med)]'
              : 'text-[color:var(--color-gray-dark)]',
          ].join(' ')}
        >
          {outcome.attribution}
        </p>
      </div>
    </motion.div>
  );
}
